import { type GetApiAdminsQueryParams } from '@/shared/api'
import { combine, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import { adminCreate, adminDelete, adminUpdate } from '../api'
import {
  type AdminsQueryModelWithPagination,
  type AdminsSortState,
  createAdminsQueryModel,
} from '../lib/admins-query-model'

interface AdminsTableGateProps {
  initialFilters?: GetApiAdminsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: AdminsSortState
}
export const AdminsTableGate = createGate<AdminsTableGateProps>()

export const AdminsTableAdminsQueryModel = createAdminsQueryModel({
  usePagination: true,
  accumulate: false,
}) as AdminsQueryModelWithPagination
const { availableSet, unavailableSet } = AdminsTableAdminsQueryModel
export const {
  //   Query
  query: adminsTableQuery,
  //   Init
  $filters: $adminsTableFilters,
  filtersInit: adminsTableFiltersInit,
  //   Search
  $search: $adminsTableSearch,
  searchChanged: adminsTableSearchChanged,
  searchReset: adminsTableSearchReset,
  //   Sort
  $sort: $adminsTableSort,
  sortChanged: adminsTableSortChanged,
  sortReset: adminsTableSortReset,
  //   Pagination
  $page: $adminsTablePage,
  pageChanged: adminsTablePageChanged,
  pageReset: adminsTablePageReset,
  $pageSize: $adminsTablePageSize,
  pageSizeChanged: adminsTablePageSizeChanged,
  //   Refresh
  refreshed: adminsTableRefreshed,
} = AdminsTableAdminsQueryModel

sample({
  clock: AdminsTableGate.open,
  target: availableSet,
})

// Search input

export const $adminsTableSearchInputValue = createStore('')
  .on(adminsTableSearchChanged, (_, q) => q)
  .reset(adminsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: AdminsTableGate.open,
  source: combine(
    AdminsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: adminsTablePageChanged,
})
// Page Size
sample({
  clock: AdminsTableGate.open,
  source: combine(
    AdminsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: adminsTablePageSizeChanged,
})
// Search
sample({
  clock: AdminsTableGate.open,
  source: combine(
    AdminsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: adminsTableSearchChanged,
})
// Sort
sample({
  clock: AdminsTableGate.open,
  source: combine(
    AdminsTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: adminsTableSortChanged,
})
// Filters
sample({
  source: AdminsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: adminsTableFiltersInit,
})

sample({
  clock: AdminsTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $adminsTablePage,
    queryData: adminsTableQuery.$data,
  }),
  clock: adminDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: adminsTablePageChanged,
})

sample({
  clock: [
    adminCreate.finished.success,
    adminUpdate.finished.success,
    adminDelete.finished.success,
  ],
  target: adminsTableRefreshed,
})
